import httpClient from '@/api/httpClient'
import ExifReader from 'exifreader'

// File formats that typically support EXIF data
const EXIF_SUPPORTED_FORMATS = ['jpeg', 'jpg', 'tiff', 'tif', 'heic', 'heif', 'dng']

// check if a file is likely to have EXIF data based on its format
const canHaveExifData = (file) => {
  // file.type returns MIME type (e.g., "image/jpeg")
  // split('/') returns an array: ["image", "jpeg"]
  // [1] gets the subtype ("jpeg", "png", etc.)
  if (!file.type) {
    console.warn('File has no MIME type, cannot determine EXIF support')
    return false
  }

  const fileType = file.type.split('/')[1]?.toLowerCase()

  if (!fileType) {
    console.warn(`Invalid MIME type format: ${file.type}`)
    return false
  }

  return EXIF_SUPPORTED_FORMATS.includes(fileType)
}

const exifFromFrontend = async (file) => {
  // Check if it's an image file (MIME type starts with "image/")
  const isImage = file.type && file.type.split('/')[0] === 'image'
  const mightHaveExif = canHaveExifData(file)

  // Skip extraction for formats known not to support EXIF
  if (!isImage || !mightHaveExif) {
    return { cutline: '', credit: '' }
  }

  try {
    const tags = await ExifReader.load(file)

    const get = (tag) => tags[tag]?.description || null

    return {
      cutline: get('description'),
      credit: get('By-line') || get('creator')
    }
  } catch (error) {
    console.warn('Error reading EXIF data from frontend:', error.message)
    return { cutline: null, credit: null }
  }
}

const exifFromBackend = async (file) => {
  // Skip backend call for formats that don't support EXIF
  if (!canHaveExifData(file)) {
    return { cutline: null, credit: null }
  }

  const postParams = new FormData()
  postParams.append('file', file)

  try {
    return await httpClient.post(
      '/api/internal/exif_data',
      postParams
    ).then(({ data: { exif_data } }) => {
      return exif_data
    })
  } catch (error) {
    console.warn('Error reading EXIF data from backend:', error.message)
    return { cutline: null, credit: null }
  }
}

const exifMediumContent = async (file) => {
  try {
    // Early return for file formats that don't support EXIF
    if (!canHaveExifData(file)) {
      console.log(`Skipping EXIF extraction for ${file.type} file - format doesn't support EXIF data (only JPEG, TIFF, HEIC, DNG formats do)`)
      return { cutline: '', credit: '' }
    }

    const frontendData = await exifFromFrontend(file)

    // If the JS library gave us all the data we're looking for, return it without going
    // through the extra overhead of calling the backend with a potentially large file.
    if(frontendData.cutline && frontendData.credit) return frontendData

    // Otherwise, call the backend to get the data
    const backendData = await exifFromBackend(file)

    // The backend uses exiftool, which is widely used and likely more reliable than the JS library.
    // So we prefer it if it's available.
    return {
      cutline: backendData.cutline || frontendData.cutline || '',
      credit: backendData.credit || frontendData.credit || ''
    }
  } catch (error) {
    console.error('Failed to extract EXIF data:', error)
    // Return empty strings as fallback to allow upload to continue
    return { cutline: '', credit: '' }
  }
}

export { exifMediumContent }
